import React, { FunctionComponent } from 'react';
import { Breakpoint, useBreakpoint } from '@noths/polaris-client-ribbons-base';
import * as R from 'ramda';

import { RadioButtonGroup } from 'src/components/molecules/RadioButtonGroup';
import { Filter, SelectedFilterOption } from 'src/types';
import { FilterOptionsProps } from '../../types';
import { FilterOptionsExpandableContent } from './FilterOptionsExpandableContent';
import { getActiveFilterCountString } from './utils';

const getSelectedRadioValue = (
  selectedFilterOptions: SelectedFilterOption[],
  filterUid?: string,
) => {
  // Note: using selectedFilterOptions to store value, not active flag
  const selectedFilterOption: SelectedFilterOption | undefined = R.find<SelectedFilterOption>(
    R.propEq('filterUid', filterUid),
  )(selectedFilterOptions);

  const selectedRadioValue: string | undefined = selectedFilterOption?.filterOptionValue as string;

  return selectedRadioValue;
};

interface Props extends Omit<FilterOptionsProps, 'currencyCode'> {
  isExpanded?: boolean;
}

export const onChange = (
  radioButtonValue: string,
  filter: Filter,
  onSelectFilterOption: FilterOptionsProps['onSelectFilterOption'],
) =>
  onSelectFilterOption({
    filterUid: filter.uid,
    filterOptionValue: radioButtonValue,
  });

export const RadioButtonGroupWrapper: FunctionComponent<Props> = ({
  filter,
  isExpanded = true,
  selectedFilterOptions,
  onSelectFilterOption,
  onExpandableContentToggle,
}: Props) => {
  const breakpoint = useBreakpoint();
  const isExpandable = filter.options.length > 5 && breakpoint !== Breakpoint.XXS;

  return (
    <FilterOptionsExpandableContent
      filter={filter}
      onExpandableContentToggle={onExpandableContentToggle}
      isExpandable={isExpandable && isExpanded}
    >
      <RadioButtonGroup
        name={filter.uid}
        accessibleLabel={filter.title}
        value={getSelectedRadioValue(selectedFilterOptions, filter.uid)}
        onChange={(radioButtonValue: string) =>
          onChange(radioButtonValue, filter, onSelectFilterOption)
        }
        radioButtonData={filter.options.map(({ title, value, activeCount }) => ({
          labelText: title,
          value: value as string,
          secondaryLabelText: getActiveFilterCountString(activeCount),
        }))}
        hideLabel
      />
    </FilterOptionsExpandableContent>
  );
};
